import React, { useState, useEffect } from "react";
import { DialogContent, Grid2, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { SlideoutPanelDialog } from "../../SlideoutPanel/SlideoutPanelDialog.component";
import { CustomColoredTabs } from "../../CustomColoredTabs/CustomColoredTabs.component";
import { CustomColoredTab } from "../../CustomColoredTabs/CustomColoredTab.component";
import { a11yProps } from "../../../features/TabPanel/TabPanel.partial";
import { TabPanel } from "../../../features/TabPanel/TabPanel.component";
import { WeekdaysSelector } from "../CapacityFilter/DateAndTypeSelector/weekdays-selector.component";
import { DepartmentSelector2 as OptionToggleSelector } from "../CapacityFilter/DateAndTypeSelector/department-selector(2).component";
import { initialWeekdays } from "../../../features/Reports/initialWeekdays.constant";
import { Weekdays } from "../../Pickers/WeekdayPicker/weekday-picker.component";
import {
  FleetFilterApplyPayload,
  FleetReportFilter
} from "../../../features/Reports/typings/fleet-capacity-report.types";
export type { FleetFilterApplyPayload } from "../../../features/Reports/typings/fleet-capacity-report.types";
import { useDepartmentOptions } from "./fleetFilterSlideout.hooks";
import type { IdName } from "./fleetFilterSlideout.hooks";

type FleetFilterSlideoutProps = {
  isOpen: boolean;
  /** Current report filter state (slideout edits weekdays and departments only). */
  filter: FleetReportFilter;
  onApply: (payload: FleetFilterApplyPayload) => void;
  onReset: () => void;
  onClose: () => void;
};

export function FleetFilterSlideout({
  isOpen,
  filter,
  onApply,
  onReset,
  onClose
}: Readonly<FleetFilterSlideoutProps>): React.ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobileTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const tableSxStyle = {
    fontSize: isMobileTablet ? 9 : 13,
    fontWeight: theme.typography.fontWeightRegular,
    pl: isMobileTablet ? 0 : 2,
    pr: isMobileTablet ? 0 : 2,
    "&.Mui-selected": {
      color:
        theme.palette.mode === "dark"
          ? theme.palette.getContrastText(theme.palette.primary.main)
          : theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold
    }
  };

  const [selectTab, setSelectTab] = useState(0);
  const [tempWeekdays, setTempWeekdays] = useState<Weekdays>(filter.weekdays);
  const [tempDepartmentSelected, setTempDepartmentSelected] = useState<IdName[]>([]);

  const departmentOpts = useDepartmentOptions();

  const handleApply = () => {
    onApply({
      departmentIds: tempDepartmentSelected.map(d => d.id),
      weekdays: tempWeekdays
    });
  };

  const handleReset = () => {
    setTempWeekdays(initialWeekdays);
    setTempDepartmentSelected([]);
    onReset();
  };

  useEffect(() => {
    if (isOpen) {
      setTempWeekdays(filter.weekdays);
      setTempDepartmentSelected(departmentOpts.filter(d => filter.departmentIds.includes(d.id)));
    }
  }, [isOpen, filter.weekdays, filter.departmentIds, departmentOpts]);

  return (
    <SlideoutPanelDialog
      isOpen={isOpen}
      dialogTitle={t("Filter")}
      width="25vw"
      height="0px"
      showCloseButton
      onClose={onClose}
      primaryButtonTitle={t("Calculate")}
      secondaryButtonTitle={t("Reset")}
      primaryButtonAction={handleApply}
      secondaryButtonAction={handleReset}
    >
      <Grid2 container data-testid="fleet-filter-slideout-tabs" mt={1.5}>
        <CustomColoredTabs value={selectTab} onChange={(_, v) => setSelectTab(v)} autoScrollable>
          <CustomColoredTab
            index={0}
            data-testid="fleet-filter-tab-weekday"
            label={t("Weekday")}
            sx={tableSxStyle}
            {...a11yProps(0)}
          />
          <CustomColoredTab
            index={1}
            data-testid="fleet-filter-tab-departments"
            label={t("Departments")}
            sx={tableSxStyle}
            {...a11yProps(1)}
          />
        </CustomColoredTabs>
      </Grid2>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1.5rem",
          padding: 0,
          overflowY: "visible"
        }}
      >
        <Grid2 container data-testid="fleet-filter-dialog-contents">
          <TabPanel value={selectTab} index={0}>
            <Grid2 container>
              <WeekdaysSelector
                weekdays={tempWeekdays}
                setWeekdaysToggle={setTempWeekdays}
                onEnableFetch={() => {}}
              />
            </Grid2>
          </TabPanel>

          <TabPanel value={selectTab} index={1}>
            <Grid2 container>
              <OptionToggleSelector
                departmentData={departmentOpts}
                departmentSelected={tempDepartmentSelected}
                selectedDepartmentIds={filter.departmentIds}
                handleDepartmentSelect={(e: any, value: { id: string; name: string }[]) =>
                  setTempDepartmentSelected(value)
                }
              />
            </Grid2>
          </TabPanel>
        </Grid2>
      </DialogContent>
    </SlideoutPanelDialog>
  );
}
